import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import apiClient from '../../utils/apiClient';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, Key, UserCheck, Users } from 'lucide-react';
import { useAuth } from '@/App';

const WardenManagement = () => {
  const navigate = useNavigate();
  const [wardens, setWardens] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingWarden, setEditingWarden] = useState(null);
  const [generatedPassword, setGeneratedPassword] = useState(null);
  const [filters, setFilters] = useState({
    includeInactive: false
  });
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    aadhar_number: '',
    email: ''
  });

  const {user} = useAuth();

  useEffect(() => {
    fetchWardens();
  }, [filters.includeInactive]);

  const fetchWardens = async () => {
    setLoading(true);
    try {
      const params = {
        hostel_id: user.hostel_id,
        include_inactive: filters.includeInactive
      };
      const response = await apiClient.get('/v1/admin/wardens/', { params });
      // Backend returns array directly
      setWardens(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      toast.error('Failed to load wardens');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (warden = null) => {
    if (warden) {
      setEditingWarden(warden);
      setFormData({
        name: warden.name,
        phone: warden.phone,
        aadhar_number: warden.aadhar_number || '',
        email: warden.email || ''
      });
    } else {
      setEditingWarden(null);
      setFormData({
        name: '',
        phone: '',
        aadhar_number: '',
        email: ''
      });
    }
    setGeneratedPassword(null);
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.name == "") {
      toast.error("Name is required");
      return;
    }
    // aadhar number must be exactly 12 digits
    const aadharRegex = /^\d{12}$/;
    if (!aadharRegex.test(formData.aadhar_number)) {
      toast.error("Aadhar number must be exactly 12 digits");
      return;
    }
    // phone must be exactly 10 digits
    const phoneRegex = /^\d{10}$/;
    if (!phoneRegex.test(formData.phone)) {
      toast.error("Phone number must be exactly 10 digits");
      return;
    }
    // email must be valid if email have
    if (formData.email && !/^\S+@\S+\.\S+$/.test(formData.email)) {
      toast.error("Invalid email format");
      return;
    }
    setLoading(true);

    try {
      if (editingWarden) {
        await apiClient.put(`/v1/admin/wardens/${editingWarden.id}`, formData);
        toast.success('Warden updated successfully');
      } else {
        const response = await apiClient.post('/v1/admin/wardens/', {
          ...formData,
          hostel_id: user.hostel_id
        });
        setGeneratedPassword(response.data.generated_password);
        toast.success(
          <div>
            <p>Warden created!</p>
            <p className="text-sm">Password: {response.data.generated_password}</p>
          </div>,
          { duration: 10000 }
        );
      }
      
      if (!editingWarden) {
        // Keep modal open to show password for new wardens
        fetchWardens();
      } else {
        setShowModal(false);
        fetchWardens();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Operation failed');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (wardenId, wardenName) => {
    if (!window.confirm(`Reset password for ${wardenName}?`)) return;

    try {
      const response = await apiClient.post(`/v1/admin/wardens/${wardenId}/reset-password`);
      toast.success(
        <div>
          <p>Password reset for {wardenName}</p>
          <p className="text-sm font-bold">New Password: {response.data.new_password}</p>
          <p className="text-xs">Please share this with the warden</p>
        </div>,
        { duration: 15000 }
      );
    } catch (error) {
      toast.error('Failed to reset password');
    }
  };

  const toggleWardenStatus = async (wardenId, currentStatus) => {
    try {
      const newStatus = !currentStatus;
      await apiClient.put(`/v1/admin/wardens/${wardenId}`, {
        is_active: newStatus
      });
      
      toast.success(`Warden ${newStatus ? 'activated' : 'deactivated'} successfully`);
      fetchWardens();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update warden status');
    }
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold" style={{ color: '#002D5B' }}>Warden Management</h1>
        <button
          onClick={() => handleOpenModal()}
          className="flex items-center gap-2 px-4 py-2 text-white rounded-lg hover:opacity-90"
          style={{ backgroundColor: '#5A9FD4' }}
        >
          <Plus className="w-4 h-4" />
          Add Warden
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <div className="flex items-center gap-4">
          <label className="flex items-center cursor-pointer">
            <input
              type="checkbox"
              className="rounded mr-2"
              checked={filters.includeInactive}
              onChange={(e) => setFilters({ ...filters, includeInactive: e.target.checked })}
            />
            <span className="text-sm font-medium text-gray-700">
              Show Inactive Wardens
            </span>
          </label>
        </div>
      </div>

      {/* Wardens List */}
      <div className="bg-white rounded-lg shadow">
        <div className="p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold">Wardens ({wardens.length})</h2>
            <button
              onClick={fetchWardens}
              className="text-blue-600 hover:text-blue-800 flex items-center gap-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              Refresh
            </button>
          </div>

          {loading ? (
            <div className="text-center py-8 text-gray-500">Loading wardens...</div>
          ) : (
            <div className="grid gap-4">
              {wardens.map(warden => (
                <div 
                  key={warden.id} 
                  className={`border rounded-lg p-4 ${
                    !warden.is_active ? 'bg-gray-50 opacity-75' : ''
                  }`}
                >
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="text-lg font-semibold">{warden.name}</h3>
                        <span className={`px-2 py-1 text-xs rounded ${
                          warden.is_active 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-red-100 text-red-800'
                        }`}>
                          {warden.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </div>
                      
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <span className="text-gray-600">Phone:</span>
                          <span className="ml-2 font-medium">{warden.phone}</span>
                        </div>
                        {warden.aadhar_number && (
                          <div>
                            <span className="text-gray-600">Aadhaar:</span>
                            <span className="ml-2 font-medium">{warden.aadhar_number}</span>
                          </div>
                        )}
                        <div>
                          <span className="text-gray-600">Assigned Students:</span>
                          <span className="ml-2 font-medium">
                            {warden.assigned_student_count || 0}
                          </span>
                        </div>
                        <div>
                          <span className="text-gray-600">Joined:</span>
                          <span className="ml-2 font-medium">
                            {new Date(warden.created_at).toLocaleDateString()}
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="flex gap-2">
                      <button
                        onClick={() => navigate(`/admin/wardens/assign/${warden.id}`)}
                        className="px-4 py-2 bg-green-50 text-green-600 rounded hover:bg-green-100 flex items-center gap-2 font-medium"
                        title="Assign Students"
                      >
                        <Users className="w-4 h-4" />
                        Assign
                      </button>
                      <button
                        onClick={() => handleOpenModal(warden)}
                        className="px-3 py-2 bg-blue-50 text-blue-600 rounded hover:bg-blue-100 flex items-center gap-1"
                        title="Edit Warden"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleResetPassword(warden.id, warden.name)}
                        className="px-3 py-2 bg-purple-50 text-purple-600 rounded hover:bg-purple-100 flex items-center gap-1"
                        title="Reset Password"
                      >
                        <Key className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => toggleWardenStatus(warden.id, warden.is_active)}
                        className={`px-4 py-2 rounded-lg font-medium ${
                          warden.is_active
                            ? 'bg-red-100 text-red-700 hover:bg-red-200'
                            : 'bg-green-100 text-green-700 hover:bg-green-200'
                        }`}
                        disabled={warden.assigned_student_count > 0}
                        title={warden.assigned_student_count > 0 ? 'Cannot deactivate: Remove assigned students first' : ''}
                      >
                        {warden.is_active ? 'Deactivate' : 'Activate'}
                      </button>
                    </div>
                  </div>
                </div>
              ))}

              {wardens.length === 0 && (
                <div className="text-center py-8 text-gray-500">
                  No wardens found
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="mt-6 bg-blue-50 rounded-lg p-4">
        <h3 className="font-semibold text-blue-900 mb-2">ℹ️ Warden Management</h3>
        <ul className="text-sm text-blue-800 space-y-1">
          <li>• Deactivating a warden prevents them from logging in</li>
          <li>• Students remain assigned to deactivated wardens</li>
          <li>• Reactivate wardens to restore their access</li>
          <li>• View assigned student count for workload management</li>
        </ul>
      </div>

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <h2 className="text-2xl font-bold mb-4" style={{ color: '#002D5B' }}>
              {editingWarden ? 'Edit Warden' : 'Add New Warden'}
            </h2>

            {generatedPassword && (
              <div className="mb-4 p-4 bg-green-50 border-l-4 border-green-500 rounded">
                <p className="text-green-800 font-semibold mb-2">✅ Warden Created Successfully!</p>
                <p className="text-sm text-green-700">Login Phone: <span className="font-bold">{formData.phone}</span></p>
                <p className="text-sm text-green-700">Password: <span className="font-bold text-lg">{generatedPassword}</span></p>
                <p className="text-xs text-green-600 mt-2">⚠️ Please save this password. It won't be shown again.</p>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Name *</label>
                <input
                  type="text"
                  required
                  className="w-full p-2 border border-gray-300 rounded-lg"
                  value={formData.name}
                  placeholder='Enter warden name'
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  disabled={generatedPassword !== null}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Phone Number (Login ID) *
                </label>
                <input
                  type="tel"
                  required
                  className="w-full p-2 border border-gray-300 rounded-lg"
                  placeholder="Enter phone number"
                  value={formData.phone}
                  onChange={(e) => setFormData({...formData, phone: e.target.value})}
                  disabled={generatedPassword !== null}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Aadhaar Number *</label>
                <input
                  type="text"
                  required
                  className="w-full p-2 border border-gray-300 rounded-lg"
                  placeholder="12 digits"
                  maxLength="12"
                  value={formData.aadhar_number}
                  onChange={(e) => setFormData({...formData, aadhar_number: e.target.value})}
                  disabled={generatedPassword !== null}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Email (Optional)</label>
                <input
                  type="email"
                  className="w-full p-2 border border-gray-300 rounded-lg"
                  value={formData.email}
                  placeholder="Enter email"
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  disabled={generatedPassword !== null}
                />
              </div>

              {!editingWarden && !generatedPassword && (
                <div className="bg-yellow-50 p-3 rounded-lg">
                  <p className="text-xs text-yellow-800">
                    🔐 A secure password will be automatically generated after creation.
                  </p>
                </div>
              )}

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowModal(false);
                    setGeneratedPassword(null);
                  }}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                >
                  {generatedPassword ? 'Close' : 'Cancel'}
                </button>
                {!generatedPassword && (
                  <button
                    type="submit"
                    disabled={loading}
                    className="flex-1 px-4 py-2 text-white rounded-lg hover:opacity-90 disabled:bg-gray-400"
                    style={{ backgroundColor: '#5A9FD4' }}
                  >
                    {loading ? 'Saving...' : editingWarden ? 'Update' : 'Create'}
                  </button>
                )}
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default WardenManagement;